import { twMerge } from 'tailwind-merge';

import { getCoverGradient } from './cover';

interface Props {
  /**
   * Stable identifier the artwork is generated from — the project number, falling back
   * to the project id for the rare project saved without one. The same seed always
   * draws the same cover, so a project stays recognisable wherever it appears.
   */
  seed: string;
  /** Project number, ghosted into the artwork. */
  projectNumber?: string;
  /** Headline over the cover — the locality on a card, the project name on a page. */
  title?: React.ReactNode;
  /** Second line under the title. */
  subtitle?: React.ReactNode;
  className?: string;
  /** Overlay content — status chips, badges, actions. Position it yourself. */
  children?: React.ReactNode;
}

/**
 * A project's generated cover. Drawn from the project number rather than fetched, so
 * every project gets its own artwork with no image to store and no request to make.
 *
 * The gradient is dark by design and the text on it is white; callers set the height
 * and corner radius through `className`.
 */
export const ProjectCover: React.FC<Props> = ({
  seed,
  projectNumber,
  title,
  subtitle,
  className,
  children,
}) => (
  <div
    className={twMerge('relative h-28 w-full overflow-hidden rounded-t-lg', className)}
    style={{ background: getCoverGradient(seed) }}
  >
    {projectNumber ? (
      <span
        aria-hidden="true"
        className="absolute left-4 top-3 text-[30px] font-bold leading-none text-white/15"
      >
        {projectNumber}
      </span>
    ) : null}

    {children}

    {title || subtitle ? (
      <div className="absolute inset-x-0 bottom-0 px-4 pb-3.5">
        {title ? <p className="line-clamp-2 text-xl font-semibold text-white">{title}</p> : null}
        {subtitle ? <p className="mt-0.5 truncate text-sm text-white/80">{subtitle}</p> : null}
      </div>
    ) : null}
  </div>
);
